import { HubItemCategoryNodeKind } from "client/Api/Hub/common";
import {
	HubItemCategoryModel,
	HubItemGroupHubSidebarNodeModel,
	HubItemHubSidebarNodeModel
} from "client/Api/Hub/hub";
import { HubItemBreadcrumbModel, KnowledgeItemDetailsViewModel } from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import { useValueEqualMemo } from "client/Hooks";
import { EMPTY_ARRAY, isNotNil } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { useMemo } from "react";

export type GroupLeaf = {
	kind: "group";
	id: number;
};

export type CategoryLeaf = {
	kind: "category";
	id: number;
};

export type ItemLeaf = {
	kind: "item";
	categoryId?: number;
};

export type BreadcrumbLeaf =
	| GroupLeaf
	| CategoryLeaf
	| ItemLeaf;

export type KnowledgeItemBreadcrumbOptions = {
	hierarchy: readonly HubItemHubSidebarNodeModel[];
	leaf: BreadcrumbLeaf | undefined;
	itemBreadcrumb?: undefined;
} | {
	itemBreadcrumb: KnowledgeItemDetailsViewModel["breadcrumb"];
	hierarchy?: undefined;
	leaf?: undefined;
};

export function useKnowledgeItemBreadcrumb(options: KnowledgeItemBreadcrumbOptions): readonly KnowledgeItemBreadcrumbSegment[] {
	const {
		hierarchy,
		itemBreadcrumb,
		leaf: leafFromOptions
	} = options;

	const leaf = useValueEqualMemo(
		(): BreadcrumbLeaf | undefined => leafFromOptions,
		[leafFromOptions]
	);

	return useMemo(
		(): readonly KnowledgeItemBreadcrumbSegment[] => {
			if (hierarchy) {
				return buildSegmentsFromHierarchy(hierarchy, leaf);
			}

			return itemBreadcrumb?.isSet && itemBreadcrumb.value
				? buildSegmentsFromItemBreadcrumb(itemBreadcrumb.value)
				: [overviewSegment, itemSegment];
		},
		[
			leaf,
			hierarchy,
			itemBreadcrumb
		]
	);
}

export function getSegmentTrackingId(
	segment: KnowledgeItemBreadcrumbSegment
): string {
	return segment.trackingId;
}

type OverviewSegment = {
	kind: "overview";
	name: string;
	trackingId: string;
};

type CategorySegment = {
	kind: "category";
	name: string;
	id: HubItemCategoryModel["id"];
	trackingId: string;
};

type GroupSegment = {
	kind: "group";
	name: string;
	id: HubItemGroupHubSidebarNodeModel["groupId"];
	trackingId: string;
};

type ItemSegment = {
	kind: "item";
	name: string;
	trackingId: string;
};

const overviewSegment: OverviewSegment = {
	kind: "overview",
	name: localize("_Overview"),
	trackingId: "overview"
};

const itemSegment: ItemSegment = {
	kind: "item",
	name: localize("_Article"),
	trackingId: "item"
};

function constructCategorySegment(
	id: number,
	name: string
): CategorySegment {
	return {
		kind: "category",
		name,
		id,
		trackingId: `category-${id}`
	};
}

function constructGroupSegment(
	id: number,
	name: string
): GroupSegment {
	return {
		kind: "group",
		name,
		id,
		trackingId: `group-${id}`
	};
}

export type KnowledgeItemClickableBreadcrumbSegment =
	| OverviewSegment
	| GroupSegment
	| CategorySegment;

export type KnowledgeItemBreadcrumbSegment =
	| OverviewSegment
	| GroupSegment
	| CategorySegment
	| ItemSegment;

function buildSegmentsFromHierarchy(
	hierarchy: readonly HubItemHubSidebarNodeModel[],
	leaf: BreadcrumbLeaf | undefined
): readonly KnowledgeItemBreadcrumbSegment[] {
	const result: KnowledgeItemBreadcrumbSegment[] = [overviewSegment];

	if (leaf) {
		switch (leaf.kind) {
			case "category":
			case "group":
				result.push(...findPathSegments(hierarchy, leaf));
				break;
			case "item":
				if (isNotNil(leaf.categoryId)) {
					result.push(
						...findPathSegments(
							hierarchy,
							{ kind: "category", id: leaf.categoryId }
						)
					);
				}

				result.push(itemSegment);
				break;
			default:
				never(leaf);
		}
	}

	return result;
}

function findPathSegments(
	hierarchy: readonly HubItemHubSidebarNodeModel[],
	leaf: GroupLeaf | CategoryLeaf
): readonly KnowledgeItemBreadcrumbSegment[] {

	for (const node of hierarchy) {
		const segments: readonly KnowledgeItemBreadcrumbSegment[] | null = findPathInternal(
			node,
			leaf,
			[]
		);

		if (segments) {
			return segments;
		}
	}

	return EMPTY_ARRAY;
}

function findPathInternal(
	currentNode: HubItemHubSidebarNodeModel,
	leaf: GroupLeaf | CategoryLeaf,
	parentSegments: readonly KnowledgeItemBreadcrumbSegment[]
): readonly KnowledgeItemBreadcrumbSegment[] | null {
	switch (currentNode.kind) {
		case HubItemCategoryNodeKind.Group:
			const currentSegments: readonly KnowledgeItemBreadcrumbSegment[] = [
				...parentSegments,
				constructGroupSegment(currentNode.groupId, currentNode.name)
			];

			if (leaf.kind === "group"
				&& currentNode.groupId === leaf.id
			) {
				return currentSegments;
			}

			for (const childNode of currentNode.children) {
				const segments: readonly KnowledgeItemBreadcrumbSegment[] | null = findPathInternal(
					childNode,
					leaf,
					currentSegments
				);

				if (segments) {
					return segments;
				}
			}

			return null;
		case HubItemCategoryNodeKind.Category:
			if (leaf.kind === "category"
				&& currentNode.category.id === leaf.id
			) {
				const { category } = currentNode;

				return [
					...parentSegments,
					constructCategorySegment(category.id, category.name)
				];
			}

			return null;
		default:
			never(currentNode);
	}
}

function buildSegmentsFromItemBreadcrumb(
	itemBreadcrumb: HubItemBreadcrumbModel
): readonly KnowledgeItemBreadcrumbSegment[] {
	const result: KnowledgeItemBreadcrumbSegment[] = [overviewSegment];

	const {
		groups,
		category
	} = itemBreadcrumb;

	for (const group of groups) {
		result.push(
			constructGroupSegment(group.id, group.name));
	}

	result.push(
		constructCategorySegment(category.id, category.name)
	);

	result.push(itemSegment);

	return result;
}
